"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { workspacesApi, type Workspace } from "@/lib/api";
import { useWorkspaceActivityAutoRefresh } from "@/hooks/useWorkspaceActivityAutoRefresh";
import {
  fetchReceivableDebtsDetails,
  resolveOwnerUserId,
  type ReceivableDebtsSummary,
  type ReceivableKind,
  type ReceivableListItem,
  type ReceivableListQuery,
} from "@/lib/pendingOrders";
import { fetchWorkspaceTags } from "@/lib/workspaceAvatarTags";
import { notifyIncomeMoneyChanged } from "@/lib/incomeMoneyRefresh";
import {
  refetchReceivablePendingWithRetry,
  subscribeReceivablePendingRefresh,
} from "@/lib/receivablePendingRefresh";

const EMPTY_SUMMARY: ReceivableDebtsSummary = {
  pendingCount: 0,
  reviewCount: 0,
  totalCount: 0,
  cardSubtitle: "ไม่มีรายการค้าง",
  detailNowLabel: "ไม่มีรายการค้างชำระ",
};

function unwrapWorkspace(data: unknown): Workspace | null {
  if (!data || typeof data !== "object") return null;
  const maybe = data as { data?: unknown; workspace?: unknown };
  const inner = maybe.data ?? maybe.workspace ?? data;
  if (!inner || typeof inner !== "object") return null;
  return inner as Workspace;
}

const EMPTY_HOT_COUNTS = {
  pending: { announcements: 0, slips: 0, addresses: 0 },
  review: { announcements: 0, slips: 0, addresses: 0 },
};

function listQueryFingerprint(query?: ReceivableListQuery): string {
  return [
    query?.kind ?? "",
    query?.dateFrom ?? "",
    query?.dateTo ?? "",
    query?.q ?? "",
    query?.status ?? "",
  ].join("|");
}

type RefetchOptions = {
  silent?: boolean;
  /** Bypass in-flight dedupe so post-match/undo never reuses a stale fetch. */
  force?: boolean;
};

export function useWorkspaceReceivableDetails(
  workspaceId?: string | null,
  workspaceProp?: Workspace | null,
  listQuery?: ReceivableListQuery,
) {
  const [workspace, setWorkspace] = useState<Workspace | null>(
    workspaceProp ?? null,
  );
  const [summary, setSummary] = useState<ReceivableDebtsSummary>(EMPTY_SUMMARY);
  const [items, setItems] = useState<ReceivableListItem[]>([]);
  const [itemsByKind, setItemsByKind] = useState<
    Record<ReceivableKind, ReceivableListItem[]>
  >({
    announcement: [],
    address: [],
    slip: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hotCounts, setHotCounts] = useState(EMPTY_HOT_COUNTS);
  const refetchGenerationRef = useRef(0);
  const pendingFingerprintRef = useRef<string | null>(null);
  const [debouncedQ, setDebouncedQ] = useState(listQuery?.q ?? "");

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQ(listQuery?.q ?? "");
    }, 300);
    return () => window.clearTimeout(timer);
  }, [listQuery?.q]);

  const effectiveQuery = useMemo<ReceivableListQuery | undefined>(() => {
    if (!listQuery) return undefined;
    return {
      kind: listQuery.kind,
      dateFrom: listQuery.dateFrom,
      dateTo: listQuery.dateTo,
      status: listQuery.status,
      q: debouncedQ,
    };
  }, [
    listQuery?.kind,
    listQuery?.dateFrom,
    listQuery?.dateTo,
    listQuery?.status,
    debouncedQ,
  ]);

  useEffect(() => {
    const wsId = workspaceProp?.id?.trim() || workspaceId?.trim() || "";
    if (!wsId) {
      setWorkspace(workspaceProp ?? null);
      return;
    }

    let cancelled = false;

    const applyWorkspace = (next: Workspace | null) => {
      if (!cancelled) setWorkspace(next);
    };

    const hasOwnerTag = (ws: Workspace | null | undefined) =>
      Boolean(
        ws?.tags?.some((tag) => tag.key?.trim() === "owner_user_id" && tag.value?.trim()),
      );

    if (workspaceProp && hasOwnerTag(workspaceProp)) {
      applyWorkspace(workspaceProp);
      return () => {
        cancelled = true;
      };
    }

    void (async () => {
      try {
        const [raw, tags] = await Promise.all([
          workspaceProp ? Promise.resolve(workspaceProp) : workspacesApi.getById(wsId),
          fetchWorkspaceTags(wsId),
        ]);
        const base = workspaceProp ?? unwrapWorkspace(raw);
        if (!base) {
          applyWorkspace(tags.length > 0 ? ({ id: wsId, name: "", tags } as Workspace) : null);
          return;
        }
        applyWorkspace({
          ...base,
          id: base.id || wsId,
          tags: tags.length > 0 ? tags : (base.tags ?? []),
        });
      } catch {
        if (workspaceProp) {
          applyWorkspace(workspaceProp);
          return;
        }
        try {
          const tags = await fetchWorkspaceTags(wsId);
          applyWorkspace(
            tags.length > 0 ? ({ id: wsId, name: "", tags } as Workspace) : ({ id: wsId } as Workspace),
          );
        } catch {
          applyWorkspace({ id: wsId } as Workspace);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [workspaceId, workspaceProp]);

  const ownerUserId = useMemo(
    () => resolveOwnerUserId(workspace),
    [workspace],
  );

  const refetch = useCallback(
    async (options?: RefetchOptions) => {
      const silent = options?.silent ?? false;
      const force = options?.force ?? false;
      // Prefer the route/workspaceId argument over workspaceProp.id — prop can lag
      // or point at another shop while the URL already switched.
      const ws = workspaceId?.trim() || workspaceProp?.id?.trim() || "";

      if (!ownerUserId) {
        // Workspace tags still resolving — stay pending so the parent does not
        // flash the empty-table state between preparing and load-failed.
        if (ws && !workspace) {
          if (!silent) setIsLoading(true);
          return;
        }
        setSummary(EMPTY_SUMMARY);
        setItems([]);
        setItemsByKind({ announcement: [], address: [], slip: [] });
        setHotCounts(EMPTY_HOT_COUNTS);
        setError(ws ? "owner_user_id is required" : null);
        setIsLoading(false);
        return;
      }

      const requestId = ++refetchGenerationRef.current;
      if (!ws) {
        setSummary(EMPTY_SUMMARY);
        setItems([]);
        setItemsByKind({ announcement: [], address: [], slip: [] });
        setHotCounts(EMPTY_HOT_COUNTS);
        setError(null);
        setIsLoading(false);
        return;
      }

      try {
        if (!silent) {
          setIsLoading(true);
          // Clear error only for visible reloads. Silent polls must keep the
          // prior failure so the shell does not drop into an empty list mid-retry.
          setError(null);
        }
        const details = await fetchReceivableDebtsDetails(ownerUserId, {
          force,
          workspaceId: ws,
          query: effectiveQuery,
        });
        // Ignore outdated responses (e.g. pre-match auto-refresh finishing after a forced post-match fetch).
        if (requestId !== refetchGenerationRef.current) return;
        setError(null);
        setSummary(details.summary);
        setItems(
          details.lists.items.map((item) => ({
            ...item,
            statuses: [...item.statuses],
            fields: [...item.fields],
          })),
        );
        setItemsByKind({
          announcement: details.lists.byKind.announcement.map((item) => ({
            ...item,
            statuses: [...item.statuses],
            fields: [...item.fields],
          })),
          address: details.lists.byKind.address.map((item) => ({
            ...item,
            statuses: [...item.statuses],
            fields: [...item.fields],
          })),
          slip: details.lists.byKind.slip.map((item) => ({
            ...item,
            statuses: [...item.statuses],
            fields: [...item.fields],
          })),
        });
        setHotCounts(details.hotCounts ?? EMPTY_HOT_COUNTS);
        // งานเงิน does not share this query — ping it when Redis pending rows change
        // so a new ยอดค้าง appears there without a full page reload.
        const fingerprint = details.lists.items
          .map((item) => item.mergeKey)
          .sort()
          .join("\n");
        const previous = pendingFingerprintRef.current;
        pendingFingerprintRef.current = fingerprint;
        if (previous !== null && previous !== fingerprint) {
          notifyIncomeMoneyChanged({ reason: "receivable_pending_changed" });
        }
      } catch (err) {
        if (requestId !== refetchGenerationRef.current) return;
        const message = err instanceof Error ? err.message : "unknown error";
        setError(message);
        // Visible failures clear rows. Silent failures keep prior rows (if any)
        // so a background poll never flashes an empty table over a hard fail.
        if (!silent) {
          setSummary(EMPTY_SUMMARY);
          setItems([]);
          setItemsByKind({ announcement: [], address: [], slip: [] });
          setHotCounts(EMPTY_HOT_COUNTS);
        }
      } finally {
        if (!silent && requestId === refetchGenerationRef.current) {
          setIsLoading(false);
        }
      }
    },
    [
      ownerUserId,
      workspace,
      workspaceId,
      workspaceProp?.id,
      listQueryFingerprint(effectiveQuery),
    ],
  );

  useEffect(() => {
    void refetch();
  }, [refetch]);

  useEffect(() => {
    pendingFingerprintRef.current = null;
  }, [workspaceId, ownerUserId]);

  useWorkspaceActivityAutoRefresh(workspaceId, () => refetch({ silent: true }));

  useEffect(() => {
    return subscribeReceivablePendingRefresh((detail) => {
      void refetchReceivablePendingWithRetry(refetch, detail);
    });
  }, [refetch]);

  return {
    ownerUserId,
    summary,
    items,
    itemsByKind,
    hotCounts,
    isLoading,
    error,
    refetch,
  };
}

export type { ReceivableListItem };
